import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Copy, ClipboardList } from "lucide-react";
import { toast } from "sonner";

export default function Sheet({ spec, tone }) {
  const [picked, setPicked] = useState([]);
  const toggle = (s) => setPicked((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  const copy = () => {
    const text = `${spec.title}\n\n${picked.map((p) => `— ${p}`).join("\n")}`;
    navigator.clipboard?.writeText(text);
    toast("Copied. Paste it into your notes or print it.");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="repro-sheet">
      <div className="lg:col-span-7">
        <p className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: tone }}>{spec.title}</p>
        <p className="text-[14.5px] mt-2 max-w-lg" style={{ color: "rgba(58,26,42,0.6)" }}>{spec.note}</p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {spec.items.map((s, i) => {
            const on = picked.includes(s);
            return (
              <motion.button key={s} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i * 0.03, 0.3) }}
                onClick={() => toggle(s)} className="text-left rounded-2xl px-4 py-3.5 flex items-start gap-3 transition-all hover:-translate-y-0.5"
                style={on ? { background: `${tone}18`, border: `1px solid ${tone}55` } : { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(58,26,42,0.07)" }}
                data-testid={`sheet-item-${i}`}>
                <span className="w-[18px] h-[18px] rounded-md flex items-center justify-center shrink-0 mt-0.5"
                  style={on ? { background: tone, color: "#fff" } : { border: "1.5px solid rgba(58,26,42,0.22)" }}>
                  {on && <Check size={11} />}
                </span>
                <span className="text-[13.5px] leading-snug" style={{ color: "#3A1A2A" }}>{s}</span>
              </motion.button>
            );
          })}
        </div>
      </div>

      <div className="lg:col-span-5">
        <div className="rounded-[1.6rem] p-6 sticky top-24" style={{ background: "#fff", border: "1px solid rgba(58,26,42,0.08)", boxShadow: "0 40px 80px -60px rgba(58,26,42,0.8)" }}>
          <div className="flex items-center gap-2.5">
            <ClipboardList size={15} style={{ color: tone }} />
            <p className="font-display text-[19px]" style={{ color: "#3A1A2A" }}>Your sheet</p>
            <p className="ml-auto font-display text-2xl tabular-nums" style={{ color: tone }}>{picked.length}</p>
          </div>
          <div className="mt-4 min-h-[120px]">
            <AnimatePresence initial={false}>
              {picked.length === 0 ? (
                <motion.p key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[13.5px]" style={{ color: "rgba(58,26,42,0.4)" }}>
                  Nothing ticked yet. Whatever you choose lands here as a numbered list you can hand over.
                </motion.p>
              ) : (
                picked.map((p, i) => (
                  <motion.p key={p} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="text-[13.5px] py-1.5 flex gap-2.5"
                    style={{ color: "#3A1A2A", borderBottom: "1px dashed rgba(58,26,42,0.08)" }}>
                    <span className="tabular-nums shrink-0" style={{ color: tone }}>{String(i + 1).padStart(2, "0")}</span>{p}
                  </motion.p>
                ))
              )}
            </AnimatePresence>
          </div>
          <button onClick={copy} disabled={!picked.length} className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold disabled:opacity-35"
            style={{ background: tone, color: "#fff" }} data-testid="sheet-copy">
            <Copy size={13} /> Copy the sheet
          </button>
        </div>
      </div>
    </div>
  );
}
